import { LINKED_ISSUE_OVERVIEW_ENTITY } from '@common/interfaces/linkedIssueOverview';
import { Injectable } from '@angular/core';
import { EntityResponse, isEntityResponse } from '@common/api/ack';
import { IBaseEntity, IBaseInterfaceData, IProjectEntity, IsPaginated, Paginated } from '@common/interfaces/base';
import { IMetaInterfaces } from '@common/interfaces/metaInterfaces';
import { RoleNames, SystemRoleNames } from '@common/interfaces/permissions';
import { Topics } from '@common/interfaces/topics';
import { ObjectHelpers } from '@common/utils/object.helpers';
import { EntityStoreAction, runEntityStoreAction, transaction } from '@datorama/akita';
import { SocketIoService } from '@ep-om/core/services/socket-io.service';
import { catchError, concatMap, filter, map, switchMap, tap, takeUntil, mergeMap } from 'rxjs/operators';
import { AuthQuery } from '../auth/auth.query';
import { AuthStore } from '../auth/auth.store';
import { AppointmentService } from './appointment/appointment.service';
import { getInterfaceStoreNamesByInterfaceName } from './interfaceDataRegistry';
import { IssueService } from './issue/issue.service';
import { LastUpdateQuery } from './lastUpdate/lastUpdate.query';
import { LastUpdateService } from './lastUpdate/lastUpdate.service';
import { MetaInterfaceServiceProvider } from './metaInterfaces/metaInterfaces.provider.service';
import { MetaInterfacesQuery } from './metaInterfaces/metaInterfaces.query';
import { ProjectQuery } from './project/project.query';
import { ProjectService } from './project/project.service';
import { ProjectScopeQuery } from './projectScope/projectScope.query';
import { UserService } from './user/user.service';
import { WorkflowQuery } from './workflow/workflow.query';
import { WorkflowDependentQuery } from './workflowDependentQuery';
import { ActionService } from './action/action.service';
import { EMPTY, from, Observable, of, Subject, Subscription } from 'rxjs';

export abstract class UpdateStoreStrategy {
  abstract start(topic: Topics, _store: string): void;

  abstract updateStore(topic: Topics, updates: { topic: Topics; body: Paginated<EntityResponse<IBaseEntity[]>> | IBaseEntity[] }): void;

  abstract isStoreLive$(storeName: string, projectId?: string): Observable<boolean>;

  @transaction()
  protected persistUpdates<T extends IBaseEntity>(updates: { topic: string; data: T[] }, storeName: string): T[] {
    const toBeDeleted = updates.data.reduce((acc, d) => {
      if (!!d.deletedAt || !!d.archivedAt) {
        acc.push(d.id);
      }
      return acc;
    }, []);
    const toBeUpserted = updates.data.filter((d) => !d.deletedAt);
    if (toBeUpserted.length > 0) {
      runEntityStoreAction(storeName, EntityStoreAction.UpsertManyEntities, (upsertManyEntities) => upsertManyEntities(toBeUpserted));
    }
    if (toBeDeleted.length > 0) {
      runEntityStoreAction(storeName, EntityStoreAction.RemoveEntities, (removeEntities) => removeEntities(toBeDeleted));
    }
    return toBeUpserted;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ProjectEntityStoreStrategy extends UpdateStoreStrategy {
  constructor(
    protected projectQuery: ProjectQuery,
    protected lastUpdateQuery: LastUpdateQuery,
    protected lastUpdateService: LastUpdateService,
    protected socketIoService: SocketIoService,
    protected workflow: WorkflowDependentQuery,
    protected metainterfacesQuery: MetaInterfacesQuery,
    protected actionService: ActionService
  ) {
    super();
    console.log('project entity store strategy constructor');
  }

  override isStoreLive$(storeName: string, projectId?: string): Observable<boolean> {
    return this.lastUpdateService.isStoreLive$(storeName, projectId);
  }

  start(topic: Topics, _storeName: string) {
    console.log('STARTING PROJECT ENTITY STORE STRATEGY', topic, _storeName);
    const storeName = topic !== Topics.META_INTERFACES_DATA ? topic : _storeName;
    this.socketIoService.uponConnection$
      .pipe(
        switchMap(() => this.projectQuery.activeNotNull$),
        switchMap((project) => this.workflow.selectInterfaceNameByWorkflow$(project.workflowId).pipe(map((interfaces) => ({ project, interfaces })))),
        filter(({ project, interfaces: projectInterfaces }) => {
          const interfaceStores = getInterfaceStoreNamesByInterfaceName();
          const isTopicAnInterface = topic === Topics.META_INTERFACES_DATA || Object.values(interfaceStores).some((store) => store === topic);
          console.log('[UPDATE] is topic', topic, 'an interface?', isTopicAnInterface);
          // project entity that are not interface must always be in sync
          if (!isTopicAnInterface) {
            return true;
          }
          //check if topic refers to an interface of the current topic, if not it should not be synced
          const isCurrentProjectInterface =
            topic !== Topics.META_INTERFACES_DATA
              ? projectInterfaces.some((intName) => interfaceStores[intName] === topic)
              : projectInterfaces.some((intName) => this.metainterfacesQuery.getInterfaceNameByValue(intName)?.value === storeName);
          console.log('[UPDATE] is topic', topic, 'an allowed interface?', isCurrentProjectInterface);
          return isCurrentProjectInterface;
        }),
        tap(({ project, interfaces }) => {
          console.log('LAST UPDATE STARTING TOPIC', topic);
          this.lastUpdateService.setStoreInSync(storeName, project.id);
        }),
        map(({ project, interfaces }) => project.id),
        concatMap(async (projectId) => {
          const getUpdates = async () => {
            const lastUpdates = this.lastUpdateQuery.getValue();
            const key = `PRJ_${projectId}_${storeName}`;
            const lastUpdateQueryParams = lastUpdates ? lastUpdates[key] : null;
            console.log('getting updates for', topic);
            if (!this.socketIoService.connected$.value) {
              throw new Error('not connected');
            }
            const result = await this.socketIoService.sendRequest<Paginated<EntityResponse<IProjectEntity[]>>>(topic, 'first_sync', {
              filters: {
                ...(topic === Topics.META_INTERFACES_DATA && { entity: storeName }),
                projectId,
                updatedAt: lastUpdateQueryParams?.lastUpdate,
                lastId: lastUpdateQueryParams?.lastId
              }
            });
            console.log('got', result);
            return result;
          };
          for (let updates = await getUpdates(); updates.body?.remaining > 0 && updates.body.data?.entities?.length > 0; updates = await getUpdates()) {
            this.updateStore(storeName, { topic, body: updates.body });
            console.log('[FIRST SYNC] - upserted -', updates.body.data.entities.length, 'entities - ', topic, 'project', projectId);
          }
          console.log('[FIRST SYNC] - completed -', topic, 'project', projectId);

          return projectId;
        }),
        catchError((err, caught) => {
          console.error(err);
          return caught;
        }),
        tap((projectId) => this.lastUpdateService.setStoreLive(storeName, projectId)),
        switchMap((projectId: string) => {
          const lastUpdates = this.lastUpdateQuery.getValue();
          const key = `PRJ_${projectId}_${storeName}`;
          return this.socketIoService.listenEntity<IProjectEntity>(topic, {
            ...(topic === Topics.META_INTERFACES_DATA && { entity: storeName }),
            projectId,
            updatedAt: lastUpdates[key].lastUpdate,
            lastId: lastUpdates[key].lastId
          });
        }),
        this.socketIoService.stopOnDisconnectionPipe(),
        this.socketIoService.repeatOnConnectionPipe()
      )
      .subscribe((updates: { topic: Topics; data: IProjectEntity[] | Paginated<EntityResponse<IProjectEntity[]>> }) =>
        this.updateStore(storeName, { topic: updates.topic, body: updates.data })
      );
  }

  updateStore(topic: string | Topics, updates: { topic: Topics; body: Paginated<EntityResponse<IProjectEntity[]>> | IProjectEntity[] }) {
    console.log('update store with updates', updates);
    let data: IProjectEntity[];
    let remaining: number;
    if (IsPaginated(updates.body)) {
      data = isEntityResponse(updates.body.data) ? updates.body.data.entities : updates.body.data;
      remaining = updates.body.remaining;
    } else {
      data = updates.body;
    }
    if (data.length === 0) {
      return;
    }
    data = data.filter((e) => !this.actionService.query.isItemChanging(e.id));      
    this.persistUpdates({ topic, data: data }, topic);
    const lastItem = data.at(-1);
    if (lastItem) {
      this.lastUpdateService.setChildLastUpdate(lastItem.projectId, topic, {
        lastId: lastItem.id,
        lastUpdate: lastItem.updatedAt,
        ...(remaining && { remaining })
      });
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class BaseEntityStoreStrategy extends UpdateStoreStrategy {
  constructor(
    protected lastUpdateQuery: LastUpdateQuery,
    protected lastUpdateService: LastUpdateService,
    protected socketIoService: SocketIoService
  ) {
    super();
  }

  override isStoreLive$(storeName: string, projectId?: string): Observable<boolean> {
    return this.lastUpdateService.isStoreLive$(storeName, projectId);
  }

  start(topic: Topics, _storeName: string) {
    this.socketIoService.uponConnection$
      .pipe(
        tap(() => this.lastUpdateService.setStoreInSync(topic)),
        concatMap(async () => {
          const getUpdates = async () => {
            const lastUpdates = this.lastUpdateQuery.getValue();
            const key = topic;
            const lastUpdateQueryParams = lastUpdates ? lastUpdates[key] : null;
            if (!this.socketIoService.connected$.value) {
              throw new Error('not connected');
            }
            return await this.socketIoService.sendRequest<Paginated<EntityResponse<IBaseEntity[]>>>(topic, 'first_sync', {
              filters: {
                updatedAt: lastUpdateQueryParams?.lastUpdate,
                lastId: lastUpdateQueryParams?.lastId
              }
            });
          };
          for (let updates = await getUpdates(); updates.body?.remaining > 0 && updates.body.data?.entities?.length > 0; updates = await getUpdates()) {
            this.updateStore(topic, { topic, body: updates.body });
            console.log('[FIRST SYNC] - upserted -', updates.body.data.entities.length, 'entities - ', topic);
          }
          console.log('[FIRST SYNC] - completed -', topic);
        }),
        catchError((err, caught) => {
          console.error(err);
          return caught;
        }),
        tap(() => this.lastUpdateService.setStoreLive(topic)),
        switchMap(() => {
          const lastUpdates = this.lastUpdateQuery.getValue();
          const key = topic;
          return this.socketIoService.listenEntity<IBaseEntity>(topic, { updatedAt: lastUpdates[key].lastUpdate, lastId: lastUpdates[key].lastId });
        }),
        this.socketIoService.stopOnDisconnectionPipe(),
        this.socketIoService.repeatOnConnectionPipe()
      )
      .subscribe((updates) => this.updateStore(topic, { topic: updates.topic, body: updates.data }));
  }
  updateStore(topic: Topics, updates: { topic: Topics; body: Paginated<EntityResponse<IBaseEntity[]>> | IBaseEntity[] }) {
    let data: IBaseEntity[];
    let remaining: number;
    if (IsPaginated(updates.body)) {
      data = isEntityResponse(updates.body.data) ? updates.body.data.entities : updates.body.data;
      remaining = updates.body.remaining;
    } else {
      data = updates.body;
    }
    if (data.length === 0) {
      return;
    }
    this.persistUpdates({ topic: updates.topic, data: data }, topic);
    const lastItem = data.at(-1);
    if (lastItem) {
      this.lastUpdateService.setLastUpdate(topic, {
        lastId: lastItem.id,
        lastUpdate: lastItem.updatedAt,
        ...(remaining && { remaining })
      });
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class MetaInterfacesUpdateStoreStrategy extends BaseEntityStoreStrategy {
  constructor(
    protected lastUpdateQuery: LastUpdateQuery,
    protected lastUpdateService: LastUpdateService,
    protected socketIoService: SocketIoService,
    protected interfaceServiceProvider: MetaInterfaceServiceProvider
  ) {
    super(lastUpdateQuery, lastUpdateService, socketIoService);
  }

  start(topic: Topics) {
    this.socketIoService.uponConnection$
      .pipe(
        tap(() => this.lastUpdateService.setStoreInSync(topic)),
        concatMap(async () => {
          const getUpdates = async () => {
            const lastUpdates = this.lastUpdateQuery.getValue();
            const key = topic;
            const lastUpdateQueryParams = lastUpdates ? lastUpdates[key] : null;
            if (!this.socketIoService.connected$.value) {
              throw new Error('not connected');
            }
            return await this.socketIoService.sendRequest<Paginated<EntityResponse<IMetaInterfaces[]>>>(topic, 'first_sync', {
              filters: {
                updatedAt: lastUpdateQueryParams?.lastUpdate,
                lastId: lastUpdateQueryParams?.lastId
              }
            });
          };
          for (let updates = await getUpdates(); updates.body.remaining > 0 && updates.body.data?.entities?.length > 0; updates = await getUpdates()) {
            this.updateStore(topic, { topic, body: updates.body });
            console.log('[FIRST SYNC] - upserted -', updates.body.data.entities.length, 'entities - ', topic);
          }
          console.log('[FIRST SYNC] - completed -', topic);
        }),
        catchError((err, caught) => {
          console.error(err);
          return caught;
        }),
        tap(() => this.lastUpdateService.setStoreLive(topic)),
        switchMap(() => {
          const lastUpdates = this.lastUpdateQuery.getValue();
          const key = topic;
          return this.socketIoService.listenEntity<IMetaInterfaces>(topic, {
            updatedAt: lastUpdates[key].lastUpdate,
            lastId: lastUpdates[key].lastId
          });
        }),
        this.socketIoService.stopOnDisconnectionPipe(),
        this.socketIoService.repeatOnConnectionPipe()
      )
      .subscribe((updates) => this.updateStore(topic, { topic: updates.topic, body: updates.data }));
  }
  updateStore(topic: Topics, updates: { topic: Topics; body: Paginated<EntityResponse<IMetaInterfaces[]>> | IMetaInterfaces[] }) {
    let data: IMetaInterfaces[];
    let remaining: number;
    if (IsPaginated(updates.body)) {
      data = isEntityResponse(updates.body.data) ? updates.body.data.entities : updates.body.data;
      remaining = updates.body.remaining;
    } else {
      data = updates.body;
    }
    if (data.length === 0) {
      return;
    }
    for (const item of data) {
      // Allineato al V2 (applyMetaInterfaceSideEffects): una metainterfaccia archiviata non deve
      // avere lo store registrato, altrimenti i due engine divergono sul set di interfacce visibili.
      if (!item.deletedAt && !item.archivedAt) {
        this.interfaceServiceProvider.addStore({ name: item.tableName, idKey: 'id' });
      } else {
        this.interfaceServiceProvider.removeStore(item.tableName);
      }
    }
    this.persistUpdates({ topic: updates.topic, data: data }, topic);
    const lastItem = data.at(-1);
    if (lastItem) {
      this.lastUpdateService.setLastUpdate(topic, {
        lastId: lastItem.id,
        lastUpdate: lastItem.updatedAt,
        ...(remaining && { remaining })
      });
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class ProjectScopeStoreStrategy extends BaseEntityStoreStrategy {
  constructor(
    protected socketIoService: SocketIoService,
    protected lastUpdateQuery: LastUpdateQuery,
    protected lastUpdateService: LastUpdateService,
    protected projectService: ProjectService,
    protected issueService: IssueService,
    protected authStore: AuthStore,
    protected authQuery: AuthQuery,
    protected userService: UserService,
    protected projectScopeQuery: ProjectScopeQuery,
    protected wfQuery: WorkflowQuery,
    protected projectQuery: ProjectQuery,
    protected appointmentService: AppointmentService
  ) {
    super(lastUpdateQuery, lastUpdateService, socketIoService);
  }

  start(topic: Topics) {
    this.socketIoService.uponConnection$
      .pipe(
        tap(() => this.lastUpdateService.setStoreInSync(topic)),
        concatMap(async () => {
          const getUpdates = async () => {
            const lastUpdates = this.lastUpdateQuery.getValue();
            const key = topic;
            const lastUpdateQueryParams = lastUpdates ? lastUpdates[key] : null;
            if (!this.socketIoService.connected$.value) {
              throw new Error('not connected');
            }
            return await this.socketIoService.sendRequest<Paginated<EntityResponse<IBaseEntity[]>>>(topic, 'first_sync', {
              filters: {
                updatedAt: lastUpdateQueryParams?.lastUpdate,
                lastId: lastUpdateQueryParams?.lastId
              }
            });
          };

          for (let updates = await getUpdates(); updates.body.remaining > 0 && updates.body.data?.entities?.length > 0; updates = await getUpdates()) {
            this.updateStore(topic, { topic, body: updates.body });
            console.log('[FIRST SYNC] - upserted -', updates.body.data.entities.length, 'entities - ', topic, 'project');
          }
          console.log('[FIRST SYNC] - completed -', topic, 'project');
          this.authStore.updateProjectRolesFromProjectScopes(this.projectScopeQuery.getAll());
        }),
        catchError((err, caught) => {
          console.error(err);
          return caught;
        }),
        tap(() => this.lastUpdateService.setStoreLive(topic)),
        switchMap(() => {
          const lastUpdates = this.lastUpdateQuery.getValue();
          const key = topic;
          return this.socketIoService.listenEntity<IBaseEntity>(topic, { updatedAt: lastUpdates[key].lastUpdate, lastId: lastUpdates[key].lastId });
        }),
        this.socketIoService.stopOnDisconnectionPipe(),
        this.socketIoService.repeatOnConnectionPipe()
      )
      .subscribe((updates) => {
        const visibilityManager = new VisibilityManager(
          this.wfQuery,
          this.projectScopeQuery,
          this.authQuery,
          this.projectQuery,
          this.appointmentService,
          this.projectService
        );
        visibilityManager.calculateStartingRoles().calculateStartingResourcePerRoles();
        this.updateStore(topic, { topic: updates.topic, body: updates.data });
        this.authStore.updateProjectRolesFromProjectScopes(this.projectScopeQuery.getAll());

        visibilityManager
          .calculateCurrentRoles()
          .calculateCurrentResourcePerRoles()
          .cleanUpAppointmentByUsers()
          .retrieveMissingAppointmentByUsers()
          .manageLimitedVisibility();
      });
  }
}

@Injectable({
  providedIn: 'root'
})
export class DummyStoreStrategy extends UpdateStoreStrategy {
  start() {}
  updateStore() {}
  isStoreLive$(): Observable<boolean> { return of(true); }
}

class VisibilityManager {
  loggedUserId: string;
  prevRoles: { [key: string]: RoleNames[] };
  prevResourceRoles: RoleNames[];
  prevUserPerResourceRole: string[];
  /**
   * key are project id
   */
  currentRoles: { [key: string]: RoleNames[] };
  currentResourceRoles: RoleNames[];
  currentUserPerResourceRole: string[];

  constructor(
    private wfQuery: WorkflowQuery,
    private projectScopeQuery: ProjectScopeQuery,
    private authQuery: AuthQuery,
    private projectQuery: ProjectQuery,
    private appointmentService?: AppointmentService,
    private projectService?: ProjectService
  ) {}

  calculateStartingRoles() {
    this.loggedUserId = this.authQuery.getLoggedUserId();
    this.prevRoles = this.authQuery.getProjectRoles();
    console.log(['CURRENT ROLES', JSON.stringify(this.prevRoles)]);
    this.prevResourceRoles = this._getResourceRoles(this.prevRoles);
    return this;
  }

  calculateStartingResourcePerRoles() {
    this.prevUserPerResourceRole = this._getUserPerResourceRole(this.prevResourceRoles);
    return this;
  }

  calculateCurrentRoles() {
    this.currentRoles = this.authQuery.getProjectRoles();
    console.log(['CURRENT ROLES', JSON.stringify(this.currentRoles)]);
    this.currentResourceRoles = this._getResourceRoles(this.currentRoles);
    return this;
  }

  calculateCurrentResourcePerRoles() {
    this.currentUserPerResourceRole = this._getUserPerResourceRole(this.currentResourceRoles);
    return this;
  }

  cleanUpAppointmentByUsers() {
    console.log('[CLEAN UP APPOINTMENTS BY USERS]', this.prevUserPerResourceRole, this.currentUserPerResourceRole);
    const personsToRemove = this.prevUserPerResourceRole.filter((user) => !this.currentUserPerResourceRole.includes(user));
    console.log('[CLEAN UP APPOINTMENTS BY USERS]', personsToRemove);
    this.appointmentService.localRemoveByUserId(personsToRemove);
    return this;
  }

  retrieveMissingAppointmentByUsers() {
    const personsToAdd = this.currentUserPerResourceRole.filter((user) => !this.prevUserPerResourceRole.includes(user));
    if (personsToAdd.length > 0) {
      this.appointmentService.remoteGet({ users: personsToAdd }, 'AppointmentRemoteGet');
    }
    return this;
  }

  manageLimitedVisibility() {
    const changes = this._calculateRoleModifications();
    console.log('role changes:', changes);
    for (const [project, { addedRoles, removedRoles, currentRoles }] of Object.entries(changes)) {
      if (
        currentRoles.length === 0 ||
        this._addedOrRemovedOutflow(currentRoles, removedRoles, addedRoles) ||
        this._addedOrRemovedLimitations(removedRoles, addedRoles)
      ) {
        this.projectService.reloadProject(project);
      }
    }
  }

  private _addedOrRemovedOutflow(currentRoles: RoleNames[], addedRoles: RoleNames[], removedRoles: RoleNames[]) {
    return (
      currentRoles.length === 1 &&
      (addedRoles?.some((role: SystemRoleNames) => ['Outflow'].includes(role)) ||
        removedRoles?.some((role: SystemRoleNames) => ['Outflow'].includes(role)))
    );
  }

  private _addedOrRemovedLimitations(addedRoles: RoleNames[], removedRoles: RoleNames[]) {
    const limitedRoles: SystemRoleNames[] = ['CompanyLimited', 'Limited', 'Outflow', 'TeamLimited', 'OloNetworkLimited'];
    return (
      addedRoles?.some((role: SystemRoleNames) => limitedRoles.includes(role)) ||
      removedRoles?.some((role: SystemRoleNames) => limitedRoles.includes(role))
    );
  }

  private _calculateRoleModifications(): { [project: string]: { addedRoles: RoleNames[]; removedRoles: RoleNames[]; currentRoles: RoleNames[] } } {
    const addedRoles: { [project: string]: Set<RoleNames> } = {};
    const removedRoles: { [project: string]: Set<RoleNames> } = {};

    const currentRoles = this.currentRoles || {};
    const prevRoles = this.prevRoles || {};

    const allProjects = new Set<string>([...Object.keys(currentRoles), ...Object.keys(prevRoles)]);

    for (const project of allProjects) {
      const current = new Set(currentRoles[project] || []);
      const prev = new Set(prevRoles[project] || []);

      for (const role of current) {
        if (!prev.has(role)) {
          if (!addedRoles[project]) addedRoles[project] = new Set();
          addedRoles[project].add(role);
        }
      }

      for (const role of prev) {
        if (!current.has(role)) {
          if (!removedRoles[project]) removedRoles[project] = new Set();
          removedRoles[project].add(role);
        }
      }
    }

    const merged: {
      [project: string]: {
        addedRoles: RoleNames[];
        currentRoles: RoleNames[];
        removedRoles: RoleNames[];
      };
    } = {};

    for (const project of allProjects) {
      merged[project] = {
        addedRoles: Array.from(addedRoles[project] || []),
        currentRoles: currentRoles[project] || [],
        removedRoles: Array.from(removedRoles[project] || [])
      };
    }

    return merged;
  }

  private _getUserPerResourceRole(roles: RoleNames[]): string[] {
    if (!roles?.length) return [];

    const roleSet = new Set(roles);
    const userIds = new Set<string>();

    for (const resource of this.projectScopeQuery.getAll()) {
      if (!resource.users?.length) continue;

      for (const user of resource.users) {
        for (const role of user.roles) {
          if (roleSet.has(role)) {
            userIds.add(user.id);
            break;
          }
        }
      }
    }

    return Array.from(userIds);
  }

  private _getResourceRoles(roles: { [key: string]: RoleNames[] }): RoleNames[] {
    if (ObjectHelpers.hasOnlyEmptyValues(roles)) return [];

    const resourceRoles = new Set<RoleNames>();

    for (const project of this.projectQuery.getAll()) {
      const workflow = this.wfQuery.getEntity(project.workflowId);
      const settings = workflow?.settings?.resourceManagement;
      const rules = settings?.rules;

      if (!settings?.enabled || !rules?.length) continue;

      const projectRoles = roles[project.id];
      if (!projectRoles?.length) continue;

      const roleSet = new Set(projectRoles);

      for (const rule of rules) {
        if (rule.managerRoles.some((role) => roleSet.has(role))) {
          for (const r of rule.resourceRoles) {
            resourceRoles.add(r);
          }
        }
      }
    }

    return Array.from(resourceRoles);
  }
}

type LinkedPreviewCfg = { interface: string; interfaceName?: string; topic: Topics; instance?: string };

//TODO: Inserire un componente a parte per gli attachment?
@Injectable({ providedIn: 'root' })
export class LinkedPreviewStoreStrategy extends UpdateStoreStrategy {
  private stop$ = new Subject<void>();

  constructor(private socketIoService: SocketIoService) { super(); }

    startForIssue(
      linkedIssueId: string,
      relatedInterfaces: LinkedPreviewCfg[],
      linkedProjectId?: string,
      parentProjectId?: string,
      taskTypeId?: string,
      interfaceType?: string,
      linkInterfaceName?: string
    ): Subscription {
      
      const sub = this.socketIoService.uponConnection$
        .pipe(
          switchMap(() => from(relatedInterfaces)),
          mergeMap(cfg => {
            const topic = cfg.topic;
            const storeName = cfg.interface.includes('_meta_') ? cfg.interface : cfg.topic;

            const entity = cfg.interface;

            return from(this.socketIoService.sendRequest<{
              enabled: boolean;
              showAttachments: boolean;
              entityName: string;
              entities: any[];
            }>(
              topic,
              'linked_preview_snapshot',
              {
                filters: {
                  issueId: linkedIssueId,
                  linkedProjectId,
                  parentProjectId,
                  taskTypeId,
                  interfaceType,
                  ...(entity ? { entity } : {}),
                  linkInterfaceName
                }
              }
            )).pipe(
              tap(resp => {
                if (resp.status === 'success' && resp.body?.enabled) {
                  this.persistUpdates(
                    { topic: topic as any, data: resp.body.entities ?? [] },
                    storeName
                  );
                }
              }),
              switchMap(resp => {
                if (resp.status !== 'success' || !resp.body?.enabled) return EMPTY;
                return this.socketIoService.listenResource<{ entityName: string; entities: any[] }>(
                  topic,
                  'linked_preview',
                  {
                    issueId: linkedIssueId,
                    linkedProjectId,
                    parentProjectId,
                    ...(entity ? { entity } : {}),
                  }
                );
              }),
              filter(msg => msg.data.entityName !== LINKED_ISSUE_OVERVIEW_ENTITY),
              tap(msg => {
                this.persistUpdates(
                  { topic: topic as any, data: msg.data.entities ?? [] },
                  storeName
                );
              }),
            );
          }),
          this.socketIoService.stopOnDisconnectionPipe(),
          this.socketIoService.repeatOnConnectionPipe(),
        )
        .subscribe();
        return sub;
    }

  stop() { this.stop$.next(); }

  start(topic: Topics, _store: string): void {}
  updateStore(): void {}
  isStoreLive$(): Observable<boolean> { return of(true); }
}

